import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createTicketSchema } from '@bstm/shared';
import type { CreateTicketInput, TicketDto } from '@bstm/shared';
import { api, ApiError } from '../api/client';
import { toast } from 'sonner';
import SellChecklistEditor from '../components/SellChecklistEditor';

export default function TicketNew() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [sellChecklist, setSellChecklist] = useState<string[]>([]);
  const [sellAllValid, setSellAllValid] = useState(false);
  const [buyMoreChecklist, setBuyMoreChecklist] = useState<string[]>([]);
  const [buyMoreAllValid, setBuyMoreAllValid] = useState(false);

  const form = useForm<CreateTicketInput>({
    resolver: zodResolver(createTicketSchema),
    defaultValues: {
      ticker: '',
      side: 'BUY',
      shares: 0,
      reason: '',
      sellCircumstances: '',
      sellChecklist: [],
      buyMoreCircumstances: '',
      buyMoreChecklist: [],
    },
  });

  const reasonValue = form.watch('reason') ?? '';
  const sideValue = form.watch('side');
  const sellCircumstancesValue = form.watch('sellCircumstances') ?? '';
  const buyMoreCircumstancesValue = form.watch('buyMoreCircumstances') ?? '';
  const isBuy = sideValue === 'BUY';

  useEffect(() => {
    form.setValue('sellChecklist', sellChecklist, { shouldValidate: true });
  }, [sellChecklist, form]);

  useEffect(() => {
    form.setValue('buyMoreChecklist', buyMoreChecklist, { shouldValidate: true });
  }, [buyMoreChecklist, form]);

  useEffect(() => {
    if (!isBuy) {
      form.setValue('sellCircumstances', undefined);
      form.setValue('sellChecklist', undefined);
      form.setValue('buyMoreCircumstances', undefined);
      form.setValue('buyMoreChecklist', undefined);
      form.clearErrors(['sellCircumstances', 'sellChecklist', 'buyMoreCircumstances', 'buyMoreChecklist']);
    }
  }, [isBuy, form]);

  const create = useMutation({
    mutationFn: (input: CreateTicketInput) =>
      api<{ ticket: TicketDto }>('/tickets', { method: 'POST', json: input }),
    onSuccess: ({ ticket }) => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      toast.success(`Ticket #${ticket.id.slice(0, 8)} submitted`);
      navigate(`/tickets/${ticket.id}`);
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed'),
  });

  function onSubmit(v: CreateTicketInput) {
    const payload: CreateTicketInput = {
      ...v,
      ticker: v.ticker.toUpperCase(),
      shares: Number(v.shares),
    };
    if (v.side === 'BUY') {
      payload.sellCircumstances = v.sellCircumstances;
      payload.sellChecklist = sellChecklist.filter((q) => q.trim().length > 0);
      payload.buyMoreCircumstances = v.buyMoreCircumstances;
      payload.buyMoreChecklist = buyMoreChecklist.filter((q) => q.trim().length > 0);
    } else {
      delete payload.sellCircumstances;
      delete payload.sellChecklist;
      delete payload.buyMoreCircumstances;
      delete payload.buyMoreChecklist;
    }
    create.mutate(payload);
  }

  const submitDisabled =
    create.isPending ||
    (isBuy &&
      (sellChecklist.length === 0 ||
        !sellAllValid ||
        buyMoreChecklist.length === 0 ||
        !buyMoreAllValid));

  function onInvalid(errors: typeof form.formState.errors) {
    const first = Object.values(errors)[0];
    const msg =
      (first && 'message' in first && typeof first.message === 'string' && first.message) ||
      'Fix the highlighted fields';
    toast.error(msg);
  }

  return (
    <div className="p-4 md:p-6 max-w-2xl">
      <h1 className="text-xl md:text-2xl font-semibold mb-4 md:mb-6">New ticket</h1>
      <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="card p-4 md:p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label" htmlFor="ticker">Ticker</label>
            <input
              id="ticker"
              data-tour="ticker"
              className="w-full font-mono uppercase"
              placeholder="AAPL"
              {...form.register('ticker', { setValueAs: (v: string) => v.toUpperCase() })}
            />
            {form.formState.errors.ticker && (
              <p className="field-error">{form.formState.errors.ticker.message}</p>
            )}
          </div>
          <div>
            <label className="label" htmlFor="side">Side</label>
            <select id="side" data-tour="side" className="w-full" {...form.register('side')}>
              <option value="BUY">BUY</option>
              <option value="SELL">SELL</option>
            </select>
          </div>
        </div>

        <div>
          <label className="label" htmlFor="shares">Shares</label>
          <input
            id="shares"
            data-tour="shares"
            type="number"
            min={1}
            step={1}
            className="w-full font-mono"
            {...form.register('shares', { valueAsNumber: true })}
          />
          {form.formState.errors.shares && (
            <p className="field-error">{form.formState.errors.shares.message}</p>
          )}
        </div>

        <div>
          <label className="label" htmlFor="reason">
            Reason / thesis
            <span className="text-zinc-500 ml-2 font-normal">{reasonValue.length} chars (min 10)</span>
          </label>
          <textarea
            id="reason"
            data-tour="reason"
            rows={4}
            className="w-full"
            placeholder="Why are we buying/selling? Cite catalyst, valuation, risk."
            {...form.register('reason')}
          />
          {form.formState.errors.reason && (
            <p className="field-error">{form.formState.errors.reason.message}</p>
          )}
        </div>

        {isBuy && (
          <>
            <div className="pt-4 border-t border-zinc-800">
              <label className="label" htmlFor="sellCircumstances">
                Under what circumstances will we sell?
                <span className="text-zinc-500 ml-2 font-normal">
                  {sellCircumstancesValue.length} chars (min 10)
                </span>
              </label>
              <textarea
                id="sellCircumstances"
                data-tour="sell-circumstances"
                rows={3}
                className="w-full"
                placeholder="Describe the exit thesis. e.g. price target, stop loss, fundamental triggers."
                {...form.register('sellCircumstances')}
              />
              {form.formState.errors.sellCircumstances && (
                <p className="field-error">{form.formState.errors.sellCircumstances.message}</p>
              )}
            </div>

            <div data-tour="sell-checklist">
              <label className="label">Sell checklist (yes/no questions)</label>
              <SellChecklistEditor
                questions={sellChecklist}
                onChange={setSellChecklist}
                onValidityChange={setSellAllValid}
                placeholder='e.g. "Has the stock dropped more than 20% from entry?"'
              />
              {sellChecklist.length === 0 && (
                <p className="field-error">Add at least one yes/no question</p>
              )}
              {sellChecklist.length > 0 && !sellAllValid && (
                <p className="field-error">All questions must be validated as yes/no before submitting</p>
              )}
            </div>

            <div className="pt-4 border-t border-zinc-800">
              <label className="label" htmlFor="buyMoreCircumstances">
                Under what circumstances will we buy more?
                <span className="text-zinc-500 ml-2 font-normal">
                  {buyMoreCircumstancesValue.length} chars (min 10)
                </span>
              </label>
              <textarea
                id="buyMoreCircumstances"
                data-tour="buy-more-circumstances"
                rows={3}
                className="w-full"
                placeholder="Describe the scale-up thesis. e.g. price pullback, fundamentals improving, conviction increase."
                {...form.register('buyMoreCircumstances')}
              />
              {form.formState.errors.buyMoreCircumstances && (
                <p className="field-error">{form.formState.errors.buyMoreCircumstances.message}</p>
              )}
            </div>

            <div data-tour="buy-more-checklist">
              <label className="label">Buy-more checklist (yes/no questions)</label>
              <SellChecklistEditor
                questions={buyMoreChecklist}
                onChange={setBuyMoreChecklist}
                onValidityChange={setBuyMoreAllValid}
                placeholder='e.g. "Has the stock pulled back more than 10% with thesis intact?"'
              />
              {buyMoreChecklist.length === 0 && (
                <p className="field-error">Add at least one yes/no question</p>
              )}
              {buyMoreChecklist.length > 0 && !buyMoreAllValid && (
                <p className="field-error">All questions must be validated as yes/no before submitting</p>
              )}
            </div>
          </>
        )}

        <div className="flex gap-2 justify-end pt-2">
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button
            type="submit"
            data-tour="submit"
            className="btn-primary"
            disabled={submitDisabled}
          >
            {create.isPending ? 'Submitting…' : 'Submit ticket'}
          </button>
        </div>
      </form>
    </div>
  );
}
